<h2
  mat-dialog-title
  class="!flex !flex-col !items-start border-none !px-6 !pb-0 !pt-5"
>
  <span class="text-base font-semibold leading-snug text-slate-900">
    Option Image Settings
  </span>
  <span class="mt-0.5 text-xs font-normal text-slate-500">
    Configuring for "{{ data.optionDisplay }}"
  </span>
</h2>

<mat-dialog-content class="!max-h-[80vh] !p-6">
  <div class="space-y-6">
    <!-- Application Scope -->
    <div>
      <label class="mb-2 block text-xs font-semibold text-slate-800">
        Apply Settings To
      </label>
      <mat-radio-group
        [(ngModel)]="scope"
        (ngModelChange)="onScopeChange($event)"
        class="grid grid-cols-2 gap-3"
        aria-label="Image settings scope"
      >
        <!-- Global Scope -->
        <div
          (click)="onScopeChange('global')"
          class="flex cursor-pointer flex-row-reverse items-start justify-between rounded-lg border p-3 text-xs transition-all"
          [class.border-blue-500]="scope === 'global'"
          [class.bg-blue-50/40]="scope === 'global'"
          [class.text-blue-950]="scope === 'global'"
          [class.border-slate-200]="scope !== 'global'"
          [class.text-slate-600]="scope !== 'global'"
        >
          <mat-radio-button
            value="global"
            color="primary"
            disableRipple
            class="pointer-events-none m-0"
          ></mat-radio-button>
          <div>
            <span class="block font-medium">Set globally</span>
            <span class="mt-0.5 block text-[11px] text-slate-500">
              Applies across all elements
            </span>
          </div>
        </div>

        <!-- Element Scope -->
        <div
          class="flex cursor-not-allowed flex-row-reverse items-start justify-between
                rounded-lg border border-slate-200 p-3 text-xs
                text-slate-400 opacity-50"
          aria-disabled="true"
          title="Only global settings are supported"
        >
          <mat-radio-button
            value="element"
            color="primary"
            disabled
            disableRipple
            class="m-0"
          ></mat-radio-button>

          <div>
            <span class="block font-medium">This element only</span>
            <span class="mt-0.5 block text-[11px] text-slate-400">
              Applies only to this option
            </span>
          </div>
        </div>
      </mat-radio-group>
    </div>

    <!-- Image Source -->
    <div>
      <div class="mb-2 flex items-center justify-between">
        <!-- Source Label on the left -->
        <label class="text-xs font-semibold text-slate-800">Source</label>

        <!-- Right-side container: Checkbox + Toggle Buttons -->
        <div class="flex items-center gap-3">
          <!-- Show option text checkbox placed before the toggle buttons -->
          <label class="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              [(ngModel)]="showOptionText"
              class="h-4 w-4 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            <span class="text-xs font-medium text-slate-700">
              Show option text
            </span>
          </label>

          <!-- Toggle Tabs -->
          <div
            class="inline-flex rounded-lg border border-slate-200 bg-slate-100 p-0.5 text-xs"
          >
            <button
              type="button"
              (click)="source = 'upload'"
              class="rounded-md px-2.5 py-1 font-medium transition-all"
              [class.bg-white]="source === 'upload'"
              [class.text-slate-900]="source === 'upload'"
              [class.shadow-xs]="source === 'upload'"
              [class.text-slate-600]="source !== 'upload'"
              [class.hover:text-slate-900]="source !== 'upload'"
            >
              Upload File
            </button>
            <button
              type="button"
              (click)="source = 'html'"
              class="rounded-md px-2.5 py-1 font-medium transition-all"
              [class.bg-white]="source === 'html'"
              [class.text-slate-900]="source === 'html'"
              [class.shadow-xs]="source === 'html'"
              [class.text-slate-600]="source !== 'html'"
              [class.hover:text-slate-900]="source !== 'html'"
            >
              Custom HTML
            </button>
          </div>
        </div>
      </div>

      <!-- Upload View -->
      @if (source === 'upload') {
        <div
          class="rounded-lg border border-dashed border-slate-300 bg-slate-50/50 p-5 text-center transition-colors"
        >
          <input
            #fileInput
            type="file"
            accept="image/*"
            class="hidden"
            (change)="onFileSelected($event)"
          />

          @if (!selectedFile && !previewUrl) {
            <mat-icon class="text-slate-400">cloud_upload</mat-icon>
            <p class="mt-1 text-xs font-medium text-slate-700">
              Upload image from device
            </p>

            <button
              type="button"
              mat-stroked-button
              class="!mt-3 !border-slate-300 !text-xs !text-slate-700 hover:!bg-white"
              (click)="fileInput.click()"
            >
              Browse files
            </button>
          }

          @if (selectedFile || previewUrl) {
            <div class="relative inline-block max-w-full">
              @if (previewUrl) {
                <img
                  [src]="previewUrl"
                  alt="Preview"
                  class="mx-auto max-h-36 rounded border border-slate-200 bg-white object-contain"
                />
              }

              <button
                type="button"
                (click)="clearImage(fileInput)"
                class="mt-3 inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-medium text-red-600 transition-colors hover:bg-red-100"
              >
                <mat-icon class="!h-4 !w-4 !text-[16px] !leading-none">
                  delete
                </mat-icon>
                <span>Remove image</span>
              </button>
            </div>

            @if (selectedFile) {
              <p
                class="mx-auto mt-2 max-w-xs truncate text-xs font-medium text-slate-600"
              >
                {{ selectedFile.name }}
              </p>
            }
          }
        </div>
      }

      <!-- Custom HTML View -->
      @if (source === 'html') {
        <div class="space-y-1">
          <textarea
            [(ngModel)]="customHtml"
            rows="5"
            spellcheck="false"
            placeholder='<img src="https://example.com/image.jpg" alt="Option image">'
            class="w-full resize-none rounded-lg border border-slate-200 p-3 font-mono text-xs text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          ></textarea>
          <p class="text-[11px] text-slate-400">
            Only use trusted, sanitized HTML markup.
          </p>
        </div>
      }
    </div>
  </div>
</mat-dialog-content>

<mat-dialog-actions
  align="end"
  class="!m-0 border-t border-slate-100 bg-slate-50/80 !px-6 !py-4"
>
  <button
    type="button"
    mat-button
    (click)="cancel()"
    class="!text-xs !text-slate-600 hover:!bg-slate-100"
  >
    Cancel
  </button>
  <button
    type="button"
    mat-flat-button
    class="!bg-blue-600 !text-xs !text-white transition-colors hover:!bg-blue-700 disabled:!bg-slate-200 disabled:!text-slate-400"
    [disabled]="
      (source === 'upload' &&
        !selectedFile &&
        !previewUrl &&
        !imageRemoved) ||
      (source === 'html' && !customHtml?.trim())
    "
    (click)="save()"
  >
    Save settings
  </button>
</mat-dialog-actions>